import { useContext, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useCancelOrder } from '@/api/order';
import { CheckoutRedirectOrderStatus } from '@/constants/checkoutRedirectOrderStatus';
import { useRedirectLink } from '@/hooks/useRedirectLink';
import { isApplePayTabMode, redirectUser } from '@/utils/location';
import { resolveIsRedirectFlow } from '@/utils/checkoutMode';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';

/**
 * The abandon path behind every "I'm leaving" control: cancel the order, then
 * either navigate back to the merchant with `status=cancel` (redirect flows)
 * or hand off to `onClose` so the SDK host tears the overlay down.
 *
 * Shared by the X button and the redirect-mode "Back to Store" link, which are
 * the same action wearing different chrome — redirect mode drops the X and
 * offers the link instead, so exactly one of them is ever mounted.
 */
export const useCancelCheckout = (onClose: () => void) => {
  const context = useContext(CheckoutContext);
  const {
    payment: { isInPaymentProcess, isExpressPaymentInProcess },
    successScreen,
    checkoutConfiguration,
  } = context;
  const { mutateAsync: cancelOrder } = useCancelOrder();
  const { fullyRedirectLink } = useRedirectLink(CheckoutRedirectOrderStatus.CANCEL);
  const isRedirectFlow = resolveIsRedirectFlow(checkoutConfiguration);
  const isClosingRef = useRef(false);
  const isPaymentInFlight = isInPaymentProcess || isExpressPaymentInProcess;

  const cancelAndClose = async () => {
    if (isPaymentInFlight) return;
    if (isClosingRef.current) return;

    // Apple Pay fallback tab: closing must NOT cancel the order — the
    // original framed checkout still runs on the same session and resumes
    // when this tab closes. Just close the window.
    if (isApplePayTabMode()) {
      trackCheckoutClosed();
      window.close();
      return;
    }

    isClosingRef.current = true;

    try {
      trackCheckoutClosed();
      if (!isInPaymentProcess || successScreen?.isOpen) {
        await cancelOrder();
      }
      if (fullyRedirectLink && isRedirectFlow) {
        onClose();
        redirectUser(fullyRedirectLink);
        return;
      }
      onClose();
    } catch (error) {
      isClosingRef.current = false;
      throw error;
    }
  };

  function trackCheckoutClosed() {
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.CHECKOUT_CLOSED, properties as Record<string, unknown>);
  }

  return { cancelAndClose, isClosingRef, isPaymentInFlight };
};
